import { useCallback, useEffect, useRef, useState } from "react";
import { type MtfReadiness, mtfApi, type CadenceProfile, type MtfBinding, type MtfDetail, type MtfRun, type MtfRuntimeState, type RunMasterOrders } from "@/lib/forex-mtf-api";
import { forexApi, type ConfigAgentV1, type EAExecutionReadiness } from "@/lib/forex-api";
import { formatRunTime, reasonLabel, roleLabel, stageReasonLabel, statusLabel, statusTone, resolveDisplayReason } from "./mtf-display";
import { RunProcess, AccountExecutionResults } from "./RunProcess";
import { candidateDirection, hasPostStrategyEvidence, runDisplayReason, stoppedAtStrategy, strategyReason } from "./mtf-display-stages";
import MasterJudgment from "./MasterJudgment";

const statuses = ["", "queued", "collecting", "full_running", "validating", "no_signal", "blocked", "failed", "expired", "shadow_only", "signal_generated"];
const strategyCategories: Record<string, string> = { trend_following: "趋势跟随", range_reversion: "区间反转", breakout: "突破" };
const decisionStyles: Record<string, string> = { conservative: "保守", balanced: "均衡", proactive: "积极" };
const setupLabels: Record<string, string> = { TREND_CONTINUATION: "趋势延续", BREAKOUT: "突破", PULLBACK_CONTINUATION: "回调延续" };

function errorMessage(error: unknown) {
  const typedError = error as { response?: { status?: number; data?: { detail?: { code?: string; message?: string; blocking_reasons?: string[] } } } };
  const blockers = typedError.response?.data?.detail?.blocking_reasons;
  if (blockers?.length) return blockers.map(reasonLabel).join("；");
  const detailMessage = typedError.response?.data?.detail?.message;
  if (detailMessage) return detailMessage;
  if (typedError.response?.status === 403) return "无权访问此智能体配置或运行记录。";
  if (typedError.response?.data?.detail?.code === "RUN_SIGNAL_IDENTITY_MISMATCH") return "本轮信号关联异常，订单暂不可用。";
  if (typedError.response?.status === 409) return "配置版本或运行准备度发生冲突，请刷新后重试。";
  if (error instanceof Error && error.message) return error.message;
  return "接口暂不可用，请检查连接后重试。";
}

function CopyButton({ value }: { value: string }) {
  const copy = () => {
    if (navigator.clipboard) void navigator.clipboard.writeText(value);
  };
  return (
    <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-[10px] text-text-muted hover:text-brand transition-colors">
      <i className="ri-file-copy-line" />复制排查编号
    </button>
  );
}

function RunMasterOrdersView({ data }: { data?: RunMasterOrders }) {
  const empty = {
    no_signal: "本轮没有生成交易信号。", no_trade: "本轮暂未确认主账号交易。",
    pending_projection: "Broker 成交已回报，订单详情同步中。",
    unverified: "主账号执行或订单关联待核对。",
    unavailable: "订单详情暂不可用，请稍后使用页面刷新。",
  };
  const money = (value: string | null, currency: string | null) =>
    value === null ? "待回报" : `${value} ${currency || "（币种待回报）"}`;
  const field = (label: string, value: string | number | null | undefined) =>
    <div className="flex gap-2 text-[11px]"><dt className="shrink-0 text-text-muted">{label}：</dt><dd className="break-words text-text-secondary">{value ?? "待回报"}</dd></div>;
  return <section aria-label="本轮主账号订单" className="rounded-xl border border-surface-border p-3 space-y-2">
    <h4 className="text-xs font-semibold text-text-primary">本轮主账号订单</h4>
    {!data && <p className="text-xs text-text-muted">当前服务暂未提供订单详情。</p>}
    {data && data.status !== "ready" && <p className="text-xs text-text-secondary">{empty[data.status]}</p>}
    {data?.reason_code === "PARTIAL_PROJECTION" && <p className="text-[11px] text-amber-700">部分成交的订单详情仍在同步。</p>}
    {data?.reason_code === "PARTIAL_UNVERIFIED" && <p className="text-[11px] text-amber-700">部分执行或订单关联仍待核对。</p>}
    {data?.truncated && <p className="text-[11px] text-amber-700">本轮共 {data.total} 笔，仅显示最近 100 笔。</p>}
    {data?.items.map((order) => {
      const closed = order.lifecycle_status === "closed";
      const profit = closed ? order.net_pnl : order.pnl;
      const profitLabel = profit === null ? "盈亏待回报" : Number(profit) > 0 ? (closed ? "净盈利" : "浮盈")
        : Number(profit) < 0 ? (closed ? "净亏损" : "浮亏") : "持平";
      const verifiedTime = order.time_status === "verified";
      return <article key={order.order_ref} className="rounded-lg bg-surface/60 p-2.5 text-xs">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <span className="font-medium text-text-primary">{order.symbol} · {order.side === "BUY" ? "买入" : "卖出"} · {closed ? "已平仓" : "持仓中"}</span>
          <span className="font-medium text-text-primary">{profitLabel} {money(profit, order.currency)}</span>
        </div>
        <p className="mt-1 text-[11px] text-text-muted">主账号 {order.account_number_masked} · 订单 {order.broker_ticket_masked}</p>
        <dl className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
          {field("初始成交手数", order.initial_filled_lot)}
          {field(closed ? "票据手数" : "剩余手数", order.lot)}
          {field("开仓价", order.open_price)}
          {field(closed ? "平仓价" : "当前价", closed ? order.close_price : order.current_price)}
          {field("开仓时间", verifiedTime && order.opened_at_utc ? formatRunTime(order.opened_at_utc) : "时间待核验")}
          {closed && field("平仓时间", verifiedTime && order.closed_at_utc ? formatRunTime(order.closed_at_utc) : "时间待核验")}
          {field("止损", order.stop_loss)}{field("止盈", order.take_profit)}
          {closed && field("交易盈亏", money(order.pnl, order.currency))}
          {closed && field("手续费", money(order.commission, order.currency))}
          {closed && field("隔夜费", money(order.swap, order.currency))}
        </dl>
        <p className="mt-2 text-[10px] text-text-muted">事实更新：{formatRunTime(order.updated_at)}{order.pnl_as_of && ` · 盈亏截至 ${formatRunTime(order.pnl_as_of)}`}{order.sync_status !== "fresh" && !closed && " · 数据可能已过期"}</p>
      </article>;
    })}
  </section>;
}

function RunCard({ run, selected, onOpen, cadenceLabel }: { run: MtfRun; selected: boolean; onOpen: () => void; cadenceLabel: string }) {
  const runReason = run.status === "shadow_only" ? "本次观察已完成；点击查看主决策建议、原因和后续状态。"
    : resolveDisplayReason(run.display_reason_code ?? run.error_code);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`w-full rounded-xl border p-3.5 text-left transition-colors ${selected ? "border-brand/40 bg-brand/5" : "border-surface-border bg-white hover:border-brand/30 hover:bg-surface/40"}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-sm font-semibold text-text-primary">{run.symbol}</span>
          <span className="text-xs text-text-muted">{run.timeframe || "策略周期"}</span>
        </div>
        <span className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-medium whitespace-nowrap ${statusTone(run.status)}`}>{statusLabel(run.status)}</span>
      </div>
      <p className="mt-2 text-xs text-text-secondary">{runReason}</p>
      {run.strategy_category && <p className="mt-1 text-[10px] text-text-muted">{strategyCategories[run.strategy_category] || run.strategy_category} · {decisionStyles[run.ai_decision_style || ""] || run.ai_decision_style || "-"} · {run.template_ref?.id || "-"}</p>}
      <div className="mt-2 flex items-center justify-between gap-3 text-[10px] text-text-muted">
        <span>{formatRunTime(run.created_at)}</span>
        <span className="truncate">{cadenceLabel}</span>
      </div>
    </button>
  );
}

export default function MasterMtfTab({ agentId, terminalId, initialRunId, configRuntime, automation, onRuntimeChanged, onEditConfig }: { agentId: string; masterAccountId: string; terminalId?: string; symbols?: string[]; initialRunId?: string; configRuntime?: { desiredState: "running" | "stopped"; controlRevision: number }; automation?: ConfigAgentV1["automation"]; onRuntimeChanged?: () => Promise<void> | void; onEditConfig?: () => void }) {
  const [binding, setBinding] = useState<MtfBinding | null>(null);
  const [bindingLoaded, setBindingLoaded] = useState(false);
  const [runsLoaded, setRunsLoaded] = useState(false);
  const [runtime, setRuntime] = useState<MtfRuntimeState | null>(null);
  const [profiles, setProfiles] = useState<CadenceProfile[]>([]);
  const [runs, setRuns] = useState<MtfRun[]>([]);
  const [detail, setDetail] = useState<MtfDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [readiness, setReadiness] = useState<MtfReadiness | null>(null);
  const [executionReadiness, setExecutionReadiness] = useState<EAExecutionReadiness | null>(null);
  const [status, setStatus] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [notice, setNotice] = useState("");
  const listScrollRef = useRef<HTMLDivElement>(null);
  const detailScrollRef = useRef<HTMLDivElement>(null);
  const detailRequestRef = useRef(0);
  const listRequestRef = useRef(0);
  const selectedRunRef = useRef<{ agentId: string; runId: string } | null>(null);

  const readDetail = useCallback(async (runId: string, preserveDetail: boolean) => {
    const requestId = ++detailRequestRef.current;
    selectedRunRef.current = { agentId, runId };
    setDetailError("");
    setDetailLoading(true);
    if (!preserveDetail) {
      setDetail(null);
      if (detailScrollRef.current) detailScrollRef.current.scrollTop = 0;
    }
    const current = () => requestId === detailRequestRef.current
      && selectedRunRef.current?.agentId === agentId
      && selectedRunRef.current?.runId === runId;
    try {
      const nextDetail = await mtfApi.detail(agentId, runId);
      if (current()) setDetail(nextDetail);
    } catch (detailFailure) {
      if (current()) {
        const statusCode = (detailFailure as { response?: { status?: number } })?.response?.status;
        if (statusCode === 403 || statusCode === 404) {
          setDetail(null);
          selectedRunRef.current = null;
        }
        setDetailError(`${errorMessage(detailFailure)}${preserveDetail && statusCode !== 403 && statusCode !== 404 ? " 当前显示上次成功的数据。" : ""}`);
      }
    } finally {
      if (requestId === detailRequestRef.current) setDetailLoading(false);
    }
  }, [agentId]);

  const load = useCallback(async ({ preserveDetail = false }: { preserveDetail?: boolean } = {}) => {
    const requestId = ++listRequestRef.current;
    const selectedAtStart = selectedRunRef.current?.agentId === agentId
      ? selectedRunRef.current.runId : initialRunId;
    if (selectedRunRef.current?.agentId !== agentId) selectedRunRef.current = null;
    setLoading(true);
    setError("");
    if (!preserveDetail) setDetail(null);
    setBindingLoaded(false);
    setRunsLoaded(false);
    setBinding(null);
    setRuntime(null);
    setReadiness(null);
    setExecutionReadiness(null);
    setRuns([]);
    setCursor(null);
    const failures: string[] = [];
    const capture = (label: string, reason: unknown) => failures.push(`${label}：${errorMessage(reason)}`);
    const [bindingResult, runsResult, capabilityResult, executionResult] = await Promise.allSettled([
      mtfApi.binding(agentId), mtfApi.runs(agentId, status), mtfApi.cadenceProfiles(),
      terminalId?.startsWith("term-auto-") ? forexApi.getExecutionReadiness(terminalId) : Promise.resolve(null),
    ]);
    if (requestId !== listRequestRef.current) return;
    if (runsResult.status === "fulfilled") {
      setRuns(runsResult.value.items);
      setCursor(runsResult.value.next_cursor);
      setRunsLoaded(true);
    } else capture("决策运行读取失败", runsResult.reason);
    if (capabilityResult.status === "fulfilled") setProfiles(capabilityResult.value.items);
    else capture("运行周期读取失败", capabilityResult.reason);
    if (executionResult.status === "fulfilled") setExecutionReadiness(executionResult.value);
    else capture("账号执行准备度读取失败", executionResult.reason);
    if (bindingResult.status === "fulfilled") {
      setBinding(bindingResult.value);
      setBindingLoaded(true);
      if (bindingResult.value) {
        const [runtimeResult, readinessResult] = await Promise.allSettled([
          mtfApi.runtime(agentId), mtfApi.readiness(agentId),
        ]);
        if (requestId !== listRequestRef.current) return;
        if (runtimeResult.status === "fulfilled") setRuntime(runtimeResult.value);
        else capture("运行状态读取失败", runtimeResult.reason);
        if (readinessResult.status === "fulfilled") setReadiness(readinessResult.value);
        else capture("研究准备度读取失败", readinessResult.reason);
      }
    } else capture("策略配置读取失败", bindingResult.reason);
    if (selectedAtStart?.startsWith("mtfrun_") &&
        (!selectedRunRef.current || selectedRunRef.current.runId === selectedAtStart)) {
      await readDetail(selectedAtStart, preserveDetail);
    }
    if (requestId !== listRequestRef.current) return;
    setError(failures.join("；"));
    setLoading(false);
  }, [agentId, terminalId, status, initialRunId, readDetail]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleRuntime = async (enabled: boolean) => {
    if (!configRuntime) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await forexApi.setConfigAgentRuntime(agentId, enabled ? "running" : "stopped",
                                           configRuntime.controlRevision, crypto.randomUUID());
      await onRuntimeChanged?.();
      await load();
      setNotice(enabled ? "智能体已开启；行情、研究和已授权账号执行将自动准备，准备完成后等待自然触发。" : "智能体已关闭新触发；持仓保护、平仓和对账继续运行。");
    } catch (toggleError) {
      setError(errorMessage(toggleError));
      await onRuntimeChanged?.();
      await load();
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    if (!cursor) return;
    setLoading(true);
    try {
      const page = await mtfApi.runs(agentId, status, cursor);
      setRuns((current) => [...current, ...page.items]);
      setCursor(page.next_cursor);
    } catch (pageError) {
      setError(errorMessage(pageError));
    } finally {
      setLoading(false);
    }
  };

  const open = async (id: string) => {
    setError("");
    await readDetail(id, false);
  };

  return (
    <section className="space-y-4" aria-label="策略研究与逐账号执行">
      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-text-primary">策略研究配置</h3>
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${runtime?.effective_enabled ? "bg-emerald-500/10 text-emerald-600" : "bg-slate-500/10 text-slate-600"}`}>
            <i className="ri-checkbox-blank-circle-fill text-[8px]" />{!runtime ? "运行状态待核对" : runtime.effective_enabled ? "实际运行中" : runtime?.requested_enabled ? "已请求，当前阻断" : "未运行"}
          </span>
        </div>

        <div className="p-5">
          {runtime?.blocking_reasons.includes("REVIEW_REQUIRED") && <div role="status" className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 text-xs text-amber-800">当前配置仍是草稿。提交并通过上架审核后，新的自然触发才会使用该版本；审核前不会运行此配置。</div>}
          {binding && runtime && binding.binding_version !== runtime.binding_version && !runtime.blocking_reasons.includes("REVIEW_REQUIRED") && <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 text-xs text-amber-800">当前编辑草稿绑定 v{binding.binding_version}；已生效绑定 v{runtime.binding_version}。审核通过前，新触发继续使用已生效版本。</div>}
          {bindingLoaded && !binding && <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 text-xs text-amber-700">请先创建并审核完整策略版本。</div>}
          {binding && <div className="grid gap-3">{binding.strategy_configs.map((config) => {
            const roles = config.timeframe_roles;
            return <section key={config.symbol} className="rounded-xl border border-surface-border p-4">
              <h4 className="text-sm font-semibold">{config.symbol} · {roles ? Object.entries(roles).map(([role, tf]) => `${({ signal: "信号", structure: "结构", context: "背景" } as Record<string, string>)[role] || role} ${tf}`).join(" / ") : "周期待核对"}</h4>
              <p className="mt-2 text-xs text-text-muted">运行周期：{profiles.find((item) => item.profile_id === config.cadence_profile_id)?.label || config.cadence_profile_id || "待核对"}。修改周期、风控或角色指令请编辑策略新版本并重新审核。</p>
            </section>;
          })}</div>}

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {onEditConfig && <button type="button" onClick={onEditConfig} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-2 text-xs font-medium text-white">编辑策略草稿</button>}
            <label className="inline-flex items-center gap-2 rounded-lg border border-surface-border bg-white px-3.5 py-2 text-xs font-medium text-text-secondary">
              <span>智能体运行</span>
              <input aria-label="智能体运行" type="checkbox" checked={configRuntime?.desiredState === "running"} disabled={saving || loading || !configRuntime || !binding} onChange={(event) => void toggleRuntime(event.target.checked)} />
            </label>
            <button type="button" onClick={() => void load({ preserveDetail: true })} disabled={loading || detailLoading} className="inline-flex items-center gap-1.5 rounded-lg border border-surface-border bg-white px-3.5 py-2 text-xs font-medium text-text-secondary hover:bg-surface disabled:opacity-50"><i className={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} />刷新</button>
          </div>
          <p className="mt-2 text-[11px] text-text-muted">开启后自动准备行情、自然触发、Fast/Full 和已授权账号执行；关闭只停止新触发，保留持仓退出与对账。</p>

          {configRuntime && <div className="mt-4 rounded-lg bg-surface/70 px-3 py-2.5 text-xs" aria-label="全链自动准备状态">
            <p className="font-medium text-text-primary">{!automation ? "状态待同步" : ({
              review_required: "等待智能体审核", stopped: "已停止新开仓", preparing: "自动准备中",
              partial_failure: "部分账号异常", waiting_trigger: "已就绪，等待自然触发", running: "自然决策运行中",
            } as const)[automation.phase]}</p>
            {automation?.reason_codes.length ? <p className="mt-1 text-amber-700">{automation.reason_codes.map(stageReasonLabel).join("；")}</p> : null}
            {automation?.accounts.map((account) => <p key={`${account.role}:${account.account_id}`} className="mt-1 text-text-secondary">
              {account.role === "master" ? "主账号" : "子账号"} {account.account_id} · {account.stage === "ready" ? "执行已就绪" : account.stage === "blocked" ? "异常" : "准备中"}
              {account.reasons.length ? ` · ${account.reasons.map(stageReasonLabel).join("；")}` : ""}
            </p>)}
          </div>}

          {readiness?.platform_execution && <div className="mt-3 rounded-lg border border-surface-border px-3 py-2.5 text-xs" aria-label="策略所需品种执行检查">
            <p className="font-medium">策略所需品种：{readiness.platform_execution.required_symbols.join('、')}</p>
            {readiness.platform_execution.symbols.map(row=><p key={row.symbol} className="mt-1 text-text-secondary">{row.symbol} · {row.status==='ready'?'执行报价已就绪':row.status==='waiting_quote'?'等待有效执行报价':row.reason_code==='ACCOUNT_SYMBOL_NOT_CONFIGURED'?'账号未配置该品种':'暂不可执行'}</p>)}
            {!readiness.platform_execution.symbols.length&&<p className="mt-1 text-amber-700">账号执行条件待验证</p>}
          </div>}

          {runtime && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
              <div className="rounded-lg bg-surface/70 px-3 py-2.5"><p className="text-[10px] text-text-muted">策略研究</p><p className="mt-1 text-xs font-medium text-text-primary">{runtime.effective_enabled ? "自然决策运行中" : runtime.requested_enabled ? "自然决策当前阻断" : "策略未运行"}</p></div>
              <div className="rounded-lg bg-surface/70 px-3 py-2.5"><p className="text-[10px] text-text-muted">平台账号准备度</p><p className={`mt-1 text-xs font-medium ${runtime.execution_ready ? "text-emerald-600" : "text-amber-600"}`}>{runtime.execution_ready == null ? "逐账号独立校验" : runtime.execution_ready ? "已就绪" : "未就绪，其他账号独立准入"}</p></div>
              <div className="rounded-lg bg-surface/70 px-3 py-2.5"><p className="text-[10px] text-text-muted">今日 Full（UTC）</p><p className="mt-1 text-xs font-medium text-text-primary">{runtime.full_usage_status === 'available' && runtime.full_usage_today !== null ? `${runtime.full_usage_today} 次` : '暂不可用'}</p></div>
              <div className="rounded-lg bg-surface/70 px-3 py-2.5"><p className="text-[10px] text-text-muted">最近修改</p><p className="mt-1 text-xs font-medium text-text-primary">{runtime.updated_by || "--"}</p><p className="text-[10px] text-text-muted">{formatRunTime(runtime.updated_at)}</p></div>
              {runtime.blocking_reasons.length > 0 && <div className="sm:col-span-4 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5"><p className="text-[10px] font-medium text-amber-700">阻断原因</p><p className="mt-1 text-[11px] text-amber-700">{runtime.blocking_reasons.map(reasonLabel).join("；")}</p></div>}
              {executionReadiness && <div className="sm:col-span-4 rounded-lg bg-surface/70 px-3 py-2.5"><p className="text-[10px] text-text-muted">主账号 EA 执行</p><p className="mt-1 text-xs font-medium text-text-primary">{executionReadiness.effective_for_new_opens ? "已就绪" : ({ ready: "条件已满足，等待执行激活", activating: "执行激活中，等待 EA 确认", blocked: "执行激活受阻", degraded: "执行已降级，等待恢复", installed: "EA 已安装，等待执行准备" } as Record<string, string>)[executionReadiness.state] || "执行尚未就绪"}</p>{executionReadiness.blocked_reasons.length > 0 && <p className="mt-1 text-[11px] text-amber-700">{executionReadiness.blocked_reasons.map(reasonLabel).join("；")}</p>}</div>}
            </div>
          )}

          {notice && <p role="status" className="mt-3 rounded-lg bg-emerald-500/5 px-3 py-2.5 text-xs text-emerald-700">{notice}</p>}
          {readiness && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                ["历史数据", readiness.history_ready, "决定快速规则是否具备足够输入"],
                ["实时行情", readiness.market_ready, "决定本次运行能否获取最新收盘数据"],
                ["深度判断条件", readiness.full_ready, "决定是否可以进入主决策智能体判断"],
              ].map(([label, ready, hint]) => (
                <div key={String(label)} className="rounded-lg bg-surface/70 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2"><span className="text-[10px] text-text-muted">{String(label)}</span><span className={`text-[10px] font-medium ${ready ? "text-emerald-600" : "text-amber-600"}`}>{ready ? "已就绪" : "等待中"}</span></div>
                  <p className="mt-1 text-[10px] leading-relaxed text-text-muted">{String(hint)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <p role="alert" className="rounded-xl border border-red-500/20 bg-red-500/5 p-3 text-xs text-red-600"><i className="ri-error-warning-line mr-2" />{error}</p>}
      {detailError && <p role="alert" className="rounded-xl border border-red-500/20 bg-red-500/5 p-3 text-xs text-red-600">{detailError}</p>}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 items-start xl:items-stretch">
        <div className="xl:col-span-2 bg-white rounded-xl border border-surface-border overflow-hidden xl:flex xl:min-h-0 xl:h-[clamp(360px,70dvh,720px)] xl:flex-col">
          <div className="px-4 py-3.5 border-b border-surface-border flex items-center justify-between gap-3 flex-wrap">
            <div><h3 id="mtf-run-list-heading" className="text-sm font-semibold text-text-primary">全部决策运行</h3><p className="text-[10px] text-text-muted mt-0.5">包含无信号、阻断、失败和未调用结果。</p></div>
            <label htmlFor="mtf-status" className="sr-only">状态筛选</label>
            <select id="mtf-status" value={status} onChange={(event) => { if (listScrollRef.current) listScrollRef.current.scrollTop = 0; setStatus(event.target.value); }} className="h-8 rounded-lg border border-surface-border bg-surface px-2.5 text-xs text-text-primary focus:border-brand/50 focus:outline-none">
              {statuses.map((item) => <option key={item} value={item}>{item ? statusLabel(item) : "全部状态"}</option>)}
            </select>
          </div>
          <div ref={listScrollRef} role="region" aria-labelledby="mtf-run-list-heading" aria-busy={loading} tabIndex={0} className="p-3 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:overscroll-y-contain focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">
            {loading && !runs.length && <p role="status" className="py-8 text-center text-xs text-text-muted"><i className="ri-loader-4-line animate-spin text-brand mr-2" />正在加载决策运行…</p>}
            {!loading && runsLoaded && !runs.length && <div className="py-10 text-center"><i className="ri-inbox-2-line text-2xl text-text-muted" /><p className="mt-2 text-xs text-text-muted">暂无自然决策运行</p></div>}
            <ul className="space-y-2">
              {runs.map((run) => <li key={run.run_id}><RunCard run={run} selected={detail?.run.run_id === run.run_id} cadenceLabel={profiles.find((item) => item.profile_id === run.cadence_profile_id)?.label || "决策运行"} onOpen={() => void open(run.run_id)} /></li>)}
            </ul>
            {cursor && <button disabled={loading} onClick={() => void next()} className="mt-3 w-full rounded-lg border border-surface-border py-2 text-xs text-text-secondary hover:bg-surface disabled:opacity-50">加载更多</button>}
          </div>
        </div>

        <div className="xl:col-span-3 bg-white rounded-xl border border-surface-border overflow-hidden xl:flex xl:min-h-0 xl:h-[clamp(360px,70dvh,720px)] xl:flex-col">
          <div className="px-4 py-3.5 border-b border-surface-border xl:shrink-0">
            <h3 id="mtf-run-detail-heading" className="text-sm font-semibold text-text-primary">本轮判断与执行</h3>
            <p className="text-[10px] text-text-muted mt-0.5">按阶段查看结论与关键执行进展。</p>
          </div>
          <div ref={detailScrollRef} role="region" aria-labelledby="mtf-run-detail-heading" aria-busy={detailLoading} tabIndex={0} className="p-4 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:overscroll-y-contain focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">
            {!detail ? (
              <div className="py-12 text-center"><i className="ri-file-search-line text-2xl text-text-muted" /><p className="mt-2 text-xs text-text-muted">{detailLoading ? "正在加载决策详情…" : "请选择一条运行查看各阶段"}</p></div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl bg-surface/60 p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div><p className="text-xs font-semibold text-text-primary">{detail.run.symbol} · {profiles.find((item) => item.profile_id === (detail.cadence_profile_id || detail.run.cadence_profile_id))?.label || detail.run.timeframe || "策略决策"}</p><p className="mt-1 text-[11px] text-text-muted">{formatRunTime(detail.run.created_at)} · {statusLabel(detail.run.status)}</p></div>
                    <CopyButton value={detail.run.run_id} />
                  </div>
                  <p className="mt-2 text-xs text-text-secondary">{runDisplayReason(detail)}</p>
                  {detail.explanation?.details.history_gaps?.map((gap) => gap.missing_ranges.map((range) => <p key={`${gap.role}-${range.expected_open}`} className="mt-1 text-[11px] text-amber-700">{gap.timeframe} 行情缺口：{formatRunTime(range.expected_open)} 至 {formatRunTime(range.next_open)}</p>))}
                </div>

                {detail.strategy_template && (
                  <section className="rounded-xl border border-surface-border bg-surface/30 p-3">
                    <h4 className="text-xs font-semibold text-text-primary">本次冻结的策略模板</h4>
                    <p className="mt-1 text-[11px] text-text-secondary">策略：{strategyCategories[detail.strategy_template.category] || detail.strategy_template.category} · 风格：{decisionStyles[detail.strategy_template.ai_decision_style] || detail.strategy_template.ai_decision_style} · 算法：{detail.strategy_template.strategy_key} · 模板：{detail.strategy_template.template_ref.id}（第 {detail.strategy_template.template_ref.version} 版）</p>
                    <p className="mt-1 text-[10px] font-mono text-text-muted break-all">规则哈希：{detail.strategy_template.rule_hash}</p>
                    {detail.strategy_candidate && <p className="mt-2 text-[11px] text-text-secondary">技术候选：{detail.strategy_candidate.status === "CANDIDATE" ? `${candidateDirection(detail) || "待确认"} · ${setupLabels[detail.strategy_candidate.setup_type || ""] || "策略候选"}` : "无交易候选"}；原因：{resolveDisplayReason(strategyReason(detail), detail.explanation)}</p>}
                  </section>
                )}

                {detail.decision_team && (
                  <section className="rounded-xl border border-surface-border p-3">
                    <h4 className="text-xs font-semibold text-text-primary">AI 决策团队 · {detail.decision_team.mode === "multi" ? "多角色" : "单角色"}</h4>
                    {stoppedAtStrategy(detail) ? (
                      <p className="mt-2 text-xs text-text-muted">策略候选未成立，本轮未进入 AI 分析。</p>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {(detail.role_results || []).map((role) => <div key={role.role_id} className="rounded-lg bg-surface/60 p-2.5">
                          <p className="text-xs font-medium text-text-primary">{role.role_id} · {statusLabel(role.status)}</p>
                          <p className="mt-1 text-[11px] text-text-secondary">{role.summary || (role.reason_code ? stageReasonLabel(role.reason_code) : "等待上一步完成")}</p>
                        </div>)}
                        <p className="text-xs text-text-secondary">{hasPostStrategyEvidence(detail) ? "主决策结果详见下方判断依据。" : "暂无主决策调用结果记录。"}</p>
                      </div>
                    )}
                  </section>
                )}

                <MasterJudgment detail={detail} />

                {(detail.full_attempts || []).some((attempt) => attempt.failure_class || attempt.output_diagnostics) && (
                  <section className="rounded-xl border border-surface-border p-3">
                    <h4 className="text-xs font-semibold text-text-primary">主决策调用诊断</h4>
                    {(detail.full_attempts || []).map((attempt) => (
                      <p key={attempt.attempt} className="mt-2 text-[11px] text-text-secondary">
                        第 {attempt.attempt} 次：{attempt.failure_class === "output_truncated" ? "输出被截断" : stageReasonLabel(attempt.reason_code) || statusLabel(attempt.status)}
                        {attempt.output_diagnostics?.finish_reason && ` · 结束原因 ${attempt.output_diagnostics.finish_reason}`}
                        {typeof attempt.output_diagnostics?.completion_tokens === "number" && ` · 输出 token ${attempt.output_diagnostics.completion_tokens}`}
                      </p>
                    ))}
                  </section>
                )}

                {(detail.admission_checks?.length || 0) > 0 && (
                  <section className="rounded-xl border border-surface-border p-3">
                    <h4 className="text-xs font-semibold text-text-primary">准入与执行前行情检查</h4>
                    <div className="mt-2 space-y-2">
                      {detail.admission_checks?.map((check, index) => (
                        <div key={`${check.phase}-${index}`} className="rounded-lg bg-surface/60 p-2.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-text-primary">{{post_full: "主决策后", pre_publish: "信号发布前", enqueue: "创建执行命令前", poll: "EA 拉取前", submitting: "EA 提交前"}[check.phase] || check.phase}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] ${statusTone(check.status)}`}>{statusLabel(check.status)}</span>
                          </div>
                          {check.reason_codes.map((code) => <p key={code} className="mt-1 text-[11px] text-text-secondary">{stageReasonLabel(code)} <span className="font-mono text-text-muted">({code})</span></p>)}
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                <RunProcess key={detail.run.run_id} detail={detail} />
                <AccountExecutionResults detail={detail}>
                  <RunMasterOrdersView data={detail.master_orders} />
                </AccountExecutionResults>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
